import { useEffect, useMemo, useState } from "react";
import { t } from "../i18n";
import { ChevronLeft, ChevronRight, CalendarPlus, MapPin, Pencil, Trash2, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useModals } from "../context/ModalsContext";
import { Shell } from "../components/Shell";
import { Sheet } from "../components/Sheet";
import { Avatar, Flag, RedFlagIcon, RED } from "../components/Avatar";
import { getLocale } from "../i18n";
import { personColor, sameDay, fmtHora, fmtFechaHora, fmtRango, tipoLabel } from "../lib/data";

const diasCorto = () => Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(getLocale(), { weekday: "narrow" }).format(new Date(2024, 0, 1 + i)).toUpperCase());
const DIA_LARGO = (d) => d.toLocaleDateString(getLocale(), { weekday: "long" });
const dayStart = (v) => { const x = new Date(v); x.setHours(0, 0, 0, 0); return x; };
const startOfWeek = (d) => { const x = dayStart(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const diffDays = (a, b) => Math.round((dayStart(a) - dayStart(b)) / 86400000);
const hasRF = (c) => !!c?.personas?.some((p) => p.red_flag);

// Convierte citas multi-día e interacciones con fecha_fin en "spans" (barras)
const toSpans = (citas, its) => [
  ...citas.filter((c) => c.fin && dayKey(new Date(c.inicio)) !== dayKey(new Date(c.fin)))
    .map((c) => ({ id: `c-${c.id}`, kind: "cita", s: dayStart(c.inicio), e: dayStart(c.fin), ini: c.inicio, fin: c.fin, color: personColor(c.person_ids[0]), data: c, label: c.personas?.map((p) => p.nombre).join(", ") || tipoLabel(c.tipo) })),
  ...its.filter((v) => v.fecha_fin)
    .map((v) => ({ id: `i-${v.id}`, kind: "it", s: dayStart(v.fecha), e: dayStart(v.fecha_fin), ini: v.fecha, fin: v.fecha_fin, color: personColor(v.person_ids[0]), data: v, label: `${tipoLabel(v.tipo)}${v.personas?.[0] ? ` · ${v.personas[0].nombre}` : ""}` })),
];

// Segmentos de una fila de 7 días con asignación de carriles
const segmentsFor = (spans, rowStart) => {
  const rowEnd = addDays(rowStart, 6);
  const segs = spans.filter((sp) => sp.e >= rowStart && sp.s <= rowEnd).map((sp) => ({
    ...sp, c0: Math.max(0, diffDays(sp.s, rowStart)), c1: Math.min(6, diffDays(sp.e, rowStart)), roundL: sp.s >= rowStart, roundR: sp.e <= rowEnd,
  })).sort((a, b) => a.c0 - b.c0 || b.c1 - a.c1);
  const lanes = [];
  segs.forEach((sg) => {
    let l = lanes.findIndex((end) => end < sg.c0);
    if (l === -1) { l = lanes.length; lanes.push(-1); }
    lanes[l] = sg.c1; sg.lane = l;
  });
  return { segs, nLanes: lanes.length };
};

function Bar({ sg, showHours, onClick, testId }) {
  const p = sg.data.personas?.[0];
  return (
    <button type="button" data-testid={testId} onClick={onClick} title={sg.label}
      style={{ gridColumn: `${sg.c0 + 1} / ${sg.c1 + 2}`, gridRow: sg.lane + 1, background: sg.color, ...(hasRF(sg.data) ? { outline: `1.5px dashed ${RED}`, outlineOffset: -1 } : {}) }}
      className={`h-[18px] min-w-0 flex items-center gap-1 px-1 text-[9px] font-medium text-[#0b0c0e] truncate ${sg.roundL ? "rounded-l-full" : ""} ${sg.roundR ? "rounded-r-full" : ""}`}>
      {sg.roundL && <Avatar nombre={p?.nombre} src={p?.foto_url} size={12} />}
      {sg.roundL && showHours && <span className="opacity-80">{fmtHora(sg.ini)}</span>}
      <span className="truncate">{sg.label}</span>
      {sg.roundR && showHours && <span className="ml-auto opacity-80 shrink-0">{fmtHora(sg.fin)}</span>}
    </button>
  );
}

function BarLanes({ spans, rowStart, showHours, onBar, prefix }) {
  const { segs, nLanes } = segmentsFor(spans, rowStart);
  if (!segs.length) return null;
  return (
    <div className="grid grid-cols-7 gap-y-0.5 px-0.5" style={{ gridTemplateRows: `repeat(${nLanes}, 18px)` }} data-testid={`${prefix}-bars`}>
      {segs.map((sg) => <Bar key={sg.id} sg={sg} showHours={showHours} onClick={() => onBar(sg)} testId={`${prefix}-bar-${sg.id}`} />)}
    </div>
  );
}

export default function Calendario() {
  const { openAppointment, openInteraction, version, bump } = useModals();
  const [vista, setVista] = useState("mes");
  const [cursor, setCursor] = useState(() => dayStart(new Date()));
  const [citas, setCitas] = useState([]);
  const [its, setIts] = useState([]);
  const [sel, setSel] = useState(null);
  const [dia, setDia] = useState(null);

  const range = useMemo(() => {
    if (vista === "semana") { const s = startOfWeek(cursor); return { start: s, end: addDays(s, 7), days: Array.from({ length: 7 }, (_, i) => addDays(s, i)) }; }
    const s = startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    return { start: s, end: addDays(s, 42), days: Array.from({ length: 42 }, (_, i) => addDays(s, i)) };
  }, [vista, cursor]);

  useEffect(() => {
    const desde = addDays(range.start, -60).toISOString();
    const hasta = range.end.toISOString();
    api.get("/appointments", { params: { desde, hasta } }).then((r) => setCitas(r.data.filter((c) => new Date(c.fin || c.inicio) >= range.start))).catch(() => {});
    api.get("/interactions", { params: { desde, hasta } }).then((r) => setIts(r.data.filter((v) => v.fecha_fin && new Date(v.fecha_fin) >= range.start))).catch(() => {});
  }, [range, version]);

  const spans = useMemo(() => toSpans(citas, its), [citas, its]);
  const singles = useMemo(() => citas.filter((c) => !c.fin || dayKey(new Date(c.inicio)) === dayKey(new Date(c.fin))), [citas]);
  const byDay = useMemo(() => {
    const m = {};
    singles.forEach((c) => { const k = dayKey(new Date(c.inicio)); (m[k] ||= []).push(c); });
    return m;
  }, [singles]);

  const nav = (n) => { setDia(null); setCursor((c) => vista === "mes" ? new Date(c.getFullYear(), c.getMonth() + n, 1) : addDays(c, 7 * n)); };
  const hoy = new Date();
  const titulo = vista === "mes" ? cursor.toLocaleDateString(getLocale(), { month: "long", year: "numeric" }) : `${range.days[0].getDate()} – ${range.days[6].toLocaleDateString(getLocale(), { day: "numeric", month: "long" })}`;
  const onBar = (sg) => (sg.kind === "cita" ? setSel(sg.data) : openInteraction(sg.data));

  const remove = async (id) => { try { await api.delete(`/appointments/${id}`); toast.success(t("appt.deleted")); setSel(null); bump(); } catch (err) { toast.error(errorMessage(err)); } };
  const resolver = async (id, realizada) => { try { await api.post(`/appointments/${id}/resolver`, { realizada }); toast.success(realizada ? t("appt.done") : t("appt.cancelled")); setSel(null); bump(); } catch (err) { toast.error(errorMessage(err)); } };

  // Lista del día: citas de un día + elementos multi-día que pasan por él
  const itemsDia = (d) => [
    ...(byDay[dayKey(d)] || []).map((c) => ({ key: `c-${c.id}`, kind: "cita", data: c, color: personColor(c.person_ids[0]), titulo: c.personas?.map((p) => p.nombre).join(", "), sub: `${fmtHora(c.inicio)} · ${c.lugar?.nombre || tipoLabel(c.tipo)}${c.lugar?.ciudad ? `, ${c.lugar.ciudad}` : ""}`, estado: c.estado })),
    ...spans.filter((sp) => d >= sp.s && d <= sp.e).map((sp) => ({ key: sp.id, kind: sp.kind, data: sp.data, color: sp.color, titulo: sp.label, dia: t("cal.dayOf", { n: diffDays(d, sp.s) + 1, m: diffDays(sp.e, sp.s) + 1 }),
      sub: `${fmtRango(sp.ini, sp.fin)}${sp.data.lugar?.nombre ? ` · ${sp.data.lugar.nombre}` : ""}${sp.data.lugar?.ciudad ? `, ${sp.data.lugar.ciudad}` : ""}`, estado: sp.data.estado })),
  ].sort((a, b) => (a.data.inicio || a.data.fecha).localeCompare(b.data.inicio || b.data.fecha));

  const ItemRow = ({ it, testId }) => (
    <li key={it.key}><button data-testid={testId} onClick={() => (it.kind === "cita" ? setSel(it.data) : openInteraction(it.data))}
      className={`surface w-full p-3 flex items-center gap-3 text-left ${it.data.tiene_cruce ? "border-[#f43f5e]/60" : ""} ${it.estado && it.estado !== "pendiente" ? "opacity-60" : ""}`} style={hasRF(it.data) ? { borderStyle: "dashed", borderColor: `${RED}aa` } : undefined}>
      <span className="w-1.5 self-stretch rounded-full" style={{ background: it.color }} />
      <Avatar nombre={it.data.personas?.[0]?.nombre} src={it.data.personas?.[0]?.foto_url} size={40} redFlag={it.data.personas?.[0]?.red_flag} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium flex items-center gap-2 truncate">{it.titulo} <Flag iso={it.data.personas?.[0]?.pais_iso} size={14} /> {it.data.tiene_cruce && <AlertTriangle size={14} className="text-[#f43f5e]" />}{hasRF(it.data) && <RedFlagIcon size={13} testId={`cita-red-flag-${it.data.id}`} />}</p>
        <p className="text-xs text-muted-foreground truncate">{it.sub}</p>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        {it.dia && <span data-testid="dia-de" className="text-[10px] rounded-full bg-secondary px-2 py-0.5">{it.dia}</span>}
        {it.estado && <span className="text-[10px] uppercase text-muted-foreground">{t(`appt.estado.${it.estado}`, { defaultValue: it.estado })}</span>}
      </div>
    </button></li>
  );

  return (
    <Shell title={t("tabs.calendario")} fab={false} right={
      <button data-testid="calendar-add-btn" onClick={() => openAppointment()} className="p-2 rounded-full hover:bg-secondary text-[#d4af37]" aria-label={t("appt.new")}><CalendarPlus size={20} /></button>
    }>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1">
          <button data-testid="calendar-prev" onClick={() => nav(-1)} className="p-2 rounded-full hover:bg-secondary"><ChevronLeft size={20} /></button>
          <button data-testid="calendar-today" onClick={() => { setDia(null); setCursor(dayStart(new Date())); }} className="chip">{t("common.today")}</button>
          <button data-testid="calendar-next" onClick={() => nav(1)} className="p-2 rounded-full hover:bg-secondary"><ChevronRight size={20} /></button>
        </div>
        <div className="flex bg-secondary rounded-full p-1" data-testid="calendar-view-toggle">
          {["mes", "semana"].map((v) => <button key={v} data-testid={`view-${v}`} onClick={() => { setVista(v); setDia(null); }} className={`px-4 py-1.5 rounded-full text-sm capitalize ${vista === v ? "bg-[#d4af37] text-[#0b0c0e]" : "text-muted-foreground"}`}>{t(v === "mes" ? "cal.month" : "cal.week")}</button>)}
        </div>
      </div>
      <h2 data-testid="calendar-title" className="text-base font-medium capitalize mb-3">{titulo}</h2>

      {vista === "mes" ? (
        <div data-testid="calendar-month" className="surface p-2">
          <div className="grid grid-cols-7 text-center text-[10px] text-muted-foreground mb-1">{diasCorto().map((d, i) => <span key={i}>{d}</span>)}</div>
          {Array.from({ length: 6 }, (_, r) => range.days.slice(r * 7, r * 7 + 7)).map((week, r) => (
            <div key={r} className="pb-1" data-testid={`month-row-${r}`}>
              <div className="grid grid-cols-7">
                {week.map((d) => {
                  const cs = byDay[dayKey(d)] || [];
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const isSel = dia && sameDay(d, dia);
                  return (
                    <button key={d.toISOString()} data-testid={`day-${d.getDate()}${inMonth ? "" : "-out"}`} onClick={() => setDia(isSel ? null : d)}
                      className={`relative min-h-[46px] rounded-lg p-1 flex flex-col items-center gap-1 ${inMonth ? "" : "opacity-30"} ${isSel ? "ring-1 ring-[#d4af37]" : ""} ${sameDay(d, hoy) ? "bg-[#d4af37]/10" : "hover:bg-secondary"}`}>
                      <span className={`text-xs ${sameDay(d, hoy) ? "text-[#f3c649] font-semibold" : ""}`}>{d.getDate()}</span>
                      <div className="flex flex-wrap justify-center gap-0.5">
                        {cs.slice(0, 3).map((c) => <span key={c.id} data-testid={c.tiene_cruce ? "dot-cruce" : "dot-cita"} className={`w-4 h-4 rounded-full overflow-hidden ${c.tiene_cruce ? "ring-2 ring-[#f43f5e]" : ""}`} style={{ background: personColor(c.person_ids[0]) }}>
                          {c.personas?.[0]?.foto_url && <img src={c.personas[0].foto_url} alt="" className="w-full h-full object-cover" />}</span>)}
                      </div>
                    </button>);
                })}
              </div>
              <BarLanes spans={spans} rowStart={week[0]} onBar={onBar} prefix={`month-row-${r}`} />
            </div>
          ))}
        </div>
      ) : (
        <div data-testid="calendar-week" className="surface p-1">
          <div className="grid grid-cols-7">
            {range.days.map((d) => <p key={d.toISOString()} className="text-center text-[10px] text-muted-foreground">{diasCorto()[(d.getDay() + 6) % 7]}<br /><span className={`text-sm ${sameDay(d, hoy) ? "text-[#f3c649] font-semibold" : "text-foreground"}`}>{d.getDate()}</span></p>)}
          </div>
          {spans.some((sp) => sp.e >= range.start && sp.s < range.end) && (
            <div className="my-1 border-y border-border py-1" data-testid="week-allday">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground px-1 mb-0.5">{t("cal.allDay")}</p>
              <BarLanes spans={spans} rowStart={range.days[0]} showHours onBar={onBar} prefix="week" />
            </div>
          )}
          <div className="grid grid-cols-7 gap-1">
            {range.days.map((d) => {
              const cs = byDay[dayKey(d)] || [];
              return (
                <div key={d.toISOString()} className={`rounded-lg p-0.5 min-h-[240px] flex flex-col gap-1 ${sameDay(d, hoy) ? "bg-[#d4af37]/5" : ""}`}>
                  {cs.map((c) => (
                    <button key={c.id} data-testid={`week-cita-${c.id}`} onClick={() => setSel(c)}
                      className={`rounded-lg p-1 text-left text-[10px] leading-tight border-l-[3px] bg-secondary ${c.tiene_cruce ? "ring-1 ring-[#f43f5e]" : ""} ${c.estado !== "pendiente" ? "opacity-50" : ""}`} style={{ borderColor: personColor(c.person_ids[0]), ...(hasRF(c) ? { outline: `1.5px dashed ${RED}`, outlineOffset: -1 } : {}) }}>
                      <div className="flex items-center gap-1"><Avatar nombre={c.personas?.[0]?.nombre} src={c.personas?.[0]?.foto_url} size={18} /><Flag iso={c.personas?.[0]?.pais_iso} size={12} />{hasRF(c) && <RedFlagIcon size={10} testId={`week-red-flag-${c.id}`} />}</div>
                      <p className="font-medium truncate mt-0.5">{fmtHora(c.inicio)}</p>
                      <p className="truncate text-muted-foreground">{c.lugar?.nombre || c.lugar?.ciudad || tipoLabel(c.tipo)}</p>
                    </button>
                  ))}
                </div>);
            })}
          </div>
        </div>
      )}

      {vista === "mes" && dia && (
        <div className="mt-4" data-testid="day-list">
          <div className="flex items-center justify-between mb-2">
            <p className="label mb-0 capitalize" data-testid="day-list-title">{dia.toLocaleDateString(getLocale(), { weekday: "long", day: "numeric", month: "long" })}</p>
            <div className="flex gap-1">
              <button data-testid="day-list-week" onClick={() => { setCursor(dia); setVista(t("cal.week")); setDia(null); }} className="chip text-xs">{t("cal.seeWeek")}</button>
              <button data-testid="day-list-close" onClick={() => setDia(null)} className="chip text-xs" aria-label={t("common.close")}><X size={12} /></button>
            </div>
          </div>
          {itemsDia(dia).length === 0 ? <p className="surface p-4 text-sm text-muted-foreground">{t("cal.nothing")}</p> : <ul className="space-y-2">{itemsDia(dia).map((it) => <ItemRow key={it.key} it={it} testId={`day-item-${it.key}`} />)}</ul>}
        </div>
      )}

      {vista === "mes" && !dia && (
        <ul className="mt-4 space-y-2" data-testid="month-list">
          {citas.filter((c) => new Date(c.inicio).getMonth() === cursor.getMonth() || new Date(c.fin || c.inicio).getMonth() === cursor.getMonth()).map((c) => (
            <ItemRow key={c.id} testId={`cita-${c.id}`} it={{ key: c.id, kind: "cita", data: c, color: personColor(c.person_ids[0]), titulo: c.personas?.map((p) => p.nombre).join(", "), estado: c.estado,
              sub: `${fmtRango(c.inicio, c.fin)} · ${c.lugar?.nombre || tipoLabel(c.tipo)}${c.lugar?.ciudad ? `, ${c.lugar.ciudad}` : ""}` }} />
          ))}
        </ul>
      )}

      <Sheet open={!!sel} onClose={() => setSel(null)} title={t("appt.title")} testId="appointment-detail-sheet">
        {sel && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar nombre={sel.personas?.[0]?.nombre} src={sel.personas?.[0]?.foto_url} size={56} redFlag={sel.personas?.[0]?.red_flag} />
              <div><p className="font-medium flex items-center gap-2">{sel.personas?.map((p) => p.nombre).join(", ")}{hasRF(sel) && <RedFlagIcon size={14} testId="detail-red-flag" />}</p>
                <p className="text-sm text-muted-foreground" data-testid="detail-rango">{fmtRango(sel.inicio, sel.fin, true)} · {tipoLabel(sel.tipo)}</p>
                {(sel.lugar?.nombre || sel.lugar?.ciudad) && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={13} /> {[sel.lugar.nombre, sel.lugar.ciudad].filter(Boolean).join(", ")}</p>}
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t(`appt.estado.${sel.estado}`, { defaultValue: sel.estado })}</span></div>
            </div>
            {sel.notas && <p className="text-sm">{sel.notas}</p>}
            {sel.conflictos?.length > 0 && <ul className="text-sm text-[#fb7185] space-y-1" data-testid="detail-conflicts">{sel.conflictos.map((c, i) => <li key={i}>• {c.mensaje}</li>)}</ul>}
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="appt-edit-btn" onClick={() => { openAppointment(sel); setSel(null); }} className="btn-ghost flex items-center justify-center gap-2 text-sm"><Pencil size={14} /> {t("common.edit")}</button>
              <button data-testid="appt-delete-btn" onClick={() => remove(sel.id)} className="btn-ghost flex items-center justify-center gap-2 text-sm text-[#f43f5e]"><Trash2 size={14} /> {t("common.remove")}</button>
              {sel.estado === "pendiente" && <>
                <button data-testid="appt-done-btn" onClick={() => resolver(sel.id, true)} className="btn-primary flex items-center justify-center gap-2 text-sm"><Check size={14} /> {t("appt.markDone")}</button>
                <button data-testid="appt-cancel-btn" onClick={() => resolver(sel.id, false)} className="btn-ghost flex items-center justify-center gap-2 text-sm"><X size={14} /> {t("appt.cancelAppt")}</button>
              </>}
            </div>
          </div>
        )}
      </Sheet>
    </Shell>
  );
}
